/**
 * Wall-clock position synthesis for remote renderers without GetPositionInfo.
 * Timer-based / buffer-and-play devices (PMG, Ideon, MU2, AirLens) need this on Tauri;
 * position-reporting UPnP renderers use Connector renderer-position-changed instead.
 */
import { isWallClockDueToStaleRelTime } from './remoteAdvanceControl';
import { isTimerBasedRenderer } from './types';

function isNetworkRenderer(zone: any): boolean {
  if (!zone?.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

/**
 * Whether the 1s wall-clock position ticker should run for the active zone.
 * Browser HTTPS: all network renderers (UPnP poller + ticker).
 * Tauri desktop: timer-based renderers and exclusive bit-perfect USB DAC playback.
 * Position-reporting UPnP renderers use Connector renderer-position-changed unless RelTime
 * has gone stale (buffer-and-play), in which case wall-clock synthesis resumes.
 * iOS native app: network renderers only (STOPGAP until native transport events land).
 */
export function shouldUseWallClockPositionTicker(
  platform: { isTauriDesktop: boolean; isIOSNativeApp?: boolean },
  zone: any,
  renderer: { id: string; name?: string } | null | undefined,
  exclusiveActive = false,
): boolean {
  // STOPGAP: revert when native transport events land — iOS never dispatches
  // maestroTransportState or position into the WebView; mirror browser HTTPS ticker.
  if (platform.isIOSNativeApp) {
    return isNetworkRenderer(zone);
  }
  if (platform.isTauriDesktop && exclusiveActive) return true;
  if (!isNetworkRenderer(zone)) return false;
  if (platform.isTauriDesktop && isWallClockDueToStaleRelTime()) return true;
  if (!platform.isTauriDesktop) return true;
  return isTimerBasedRenderer(renderer, zone);
}

export type SyntheticPositionTickResult =
  | { action: 'idle' }
  | { action: 'reanchor'; anchorTimeMs: number; anchorPosition: number }
  | { action: 'update'; position: number; duration: number };

export type WallClockTickCycleInput = {
  platform: { isTauriDesktop: boolean; isIOSNativeApp?: boolean };
  zone: any;
  renderer: { id: string; name?: string } | null | undefined;
  exclusiveActive?: boolean;
  playbackState: string;
  currentTrack: unknown;
  position: number;
  duration: number;
  anchorTimeMs: number;
  anchorPosition: number;
  nowMs: number;
};

export type WallClockTickCycleResult = {
  anchorTimeMs: number;
  anchorPosition: number;
  storeWrite: { position: number; duration: number } | null;
};

/**
 * One wall-clock tick cycle — gate check, anchor establishment, synthetic position,
 * and optional store write. Shared by useRemotePositionTracker and golden tests.
 */
export function runWallClockPositionTickCycle(
  input: WallClockTickCycleInput,
): WallClockTickCycleResult {
  const gate = shouldUseWallClockPositionTicker(
    input.platform,
    input.zone,
    input.renderer,
    input.exclusiveActive ?? false,
  );

  if (!gate) {
    return { anchorTimeMs: 0, anchorPosition: 0, storeWrite: null };
  }

  let anchorTimeMs = input.anchorTimeMs;
  let anchorPosition = input.anchorPosition;

  if (input.playbackState === 'playing' && input.currentTrack && anchorTimeMs === 0) {
    anchorTimeMs = input.nowMs;
    anchorPosition = input.position || 0;
  }

  const result = tickSyntheticRemotePosition({
    playbackState: input.playbackState,
    currentTrack: input.currentTrack,
    position: input.position,
    duration: input.duration,
    anchorTimeMs,
    anchorPosition,
    nowMs: input.nowMs,
  });

  if (result.action === 'reanchor') {
    return {
      anchorTimeMs: result.anchorTimeMs,
      anchorPosition: result.anchorPosition,
      storeWrite: null,
    };
  }

  if (result.action === 'update') {
    return {
      anchorTimeMs,
      anchorPosition,
      storeWrite: { position: result.position, duration: result.duration },
    };
  }

  return { anchorTimeMs, anchorPosition, storeWrite: null };
}

/**
 * One 1s tick of synthetic position — shared by useRemotePositionTracker and tests.
 */
export function tickSyntheticRemotePosition(input: {
  playbackState: string;
  currentTrack: unknown;
  position: number;
  duration: number;
  anchorTimeMs: number;
  anchorPosition: number;
  nowMs: number;
}): SyntheticPositionTickResult {
  const {
    playbackState,
    currentTrack,
    position,
    duration,
    anchorTimeMs,
    anchorPosition,
    nowMs,
  } = input;

  if (playbackState !== 'playing' || !currentTrack) {
    return { action: 'idle' };
  }

  let anchorTime = anchorTimeMs;
  let anchorPos = anchorPosition;
  if (anchorTime === 0) {
    anchorTime = nowMs;
    anchorPos = position || 0;
  }

  const elapsed = (nowMs - anchorTime) / 1000;
  const newPos = anchorPos + elapsed;
  const clamped = duration > 0 ? Math.min(newPos, duration) : newPos;

  const expectedDelta = Math.abs(clamped - position);
  if (expectedDelta > 3) {
    return {
      action: 'reanchor',
      anchorTimeMs: nowMs,
      anchorPosition: position,
    };
  }

  if (Math.floor(clamped) !== Math.floor(position)) {
    return { action: 'update', position: clamped, duration };
  }

  return { action: 'idle' };
}
